import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from './AppLayout';
import { buttonStyle, cardTitle, inputStyle, labelStyle, subtitle } from './quoteStyles';

const occupationOptions = [
  'Software Engineer',
  'Teacher',
  'Nurse',
  'Accountant',
  'Sales Executive',
  'Driver',
  'Business Owner',
  'Student',
  'Retired',
  'Other',
];

const CustomerDetailsScreen = () => {
  const navigate = useNavigate();
  const { applicationId } = useParams();

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    postcode: '',
    gender: '',
    smoker: '',
    occupation: '',
  });
  const [error, setError] = useState('');

  const isAdult = value => {
    const dob = new Date(value);
    if (Number.isNaN(dob.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDelta = today.getMonth() - dob.getMonth();
    if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) age -= 1;
    return age >= 18 && age <= 100;
  };

  const validate = () => {
    if (form.fullName.trim().length < 2) return 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Please enter a valid email address.';
    if (!/^[0-9+\-\s()]{8,20}$/.test(form.phone.trim())) return 'Please enter a valid phone number.';
    if (!isAdult(form.dateOfBirth)) return 'Applicant must be between 18 and 100 years old.';
    if (!/^[0-9]{4}$/.test(form.postcode.trim())) return 'Postcode must be a 4-digit number.';
    if (!['female', 'male', 'prefer_not_to_say'].includes(form.gender)) return 'Please select gender.';
    if (form.smoker === '') return 'Please tell us whether you smoke.';
    if (form.occupation.trim().length < 2) return 'Please enter occupation.';
    return '';
  };

  useEffect(() => {
    const loadApplication = async () => {
      const response = await fetch(`/api/application/${applicationId}`);
      if (!response.ok) {
        navigate('/quote/start');
        return;
      }

      const data = await response.json();
      if (data.customer) {
        setForm({
          fullName: data.customer.fullName || '',
          email: data.customer.email || '',
          phone: data.customer.phone || '',
          dateOfBirth: data.customer.dateOfBirth || '',
          postcode: data.customer.postcode || '',
          gender: data.customer.gender || '',
          smoker: typeof data.customer.smoker === 'boolean' ? data.customer.smoker : '',
          occupation: data.customer.occupation || '',
        });
      }
    };

    loadApplication();
  }, [applicationId, navigate]);

  const saveAndContinue = async event => {
    event.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const response = await fetch(`/api/application/${applicationId}/customer`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        dateOfBirth: form.dateOfBirth.trim(),
        postcode: form.postcode.trim(),
        gender: form.gender,
        smoker: form.smoker,
        occupation: form.occupation.trim(),
      }),
    });

    const data = await response.json().catch(() => ({ error: 'Unexpected server response' }));
    if (!response.ok) {
      setError(data.error || 'Unable to save customer details');
      return;
    }

    navigate(`/quote/${applicationId}/coverage`);
  };

  return (
    <AppLayout>
      <h1 style={cardTitle}>Your Details</h1>
      <p style={subtitle}>Step 2 of 4: Enter your details and email for quote delivery.</p>
      {error && <div style={styles.errorBanner}>{error}</div>}

      <form onSubmit={saveAndContinue} style={styles.grid}>
        <div>
          <label style={labelStyle}>Full name</label>
          <input
            required
            value={form.fullName}
            onChange={event => setForm({ ...form, fullName: event.target.value })}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={event => setForm({ ...form, email: event.target.value })}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Phone</label>
          <input
            required
            value={form.phone}
            onChange={event => setForm({ ...form, phone: event.target.value })}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Date of birth</label>
          <input
            type="date"
            required
            value={form.dateOfBirth}
            onChange={event => setForm({ ...form, dateOfBirth: event.target.value })}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Gender</label>
          <select
            required
            value={form.gender}
            onChange={event => setForm({ ...form, gender: event.target.value })}
            style={inputStyle}
          >
            <option value="">Select one</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Do you smoke?</label>
          <select
            required
            value={form.smoker === '' ? '' : form.smoker ? 'yes' : 'no'}
            onChange={event => setForm({ ...form, smoker: event.target.value === 'yes' })}
            style={inputStyle}
          >
            <option value="">Select one</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Occupation</label>
          <select
            required
            value={form.occupation}
            onChange={event => setForm({ ...form, occupation: event.target.value })}
            style={inputStyle}
          >
            <option value="">Select one</option>
            {occupationOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Postcode</label>
          <input
            required
            value={form.postcode}
            onChange={event => setForm({ ...form, postcode: event.target.value })}
            style={inputStyle}
          />
        </div>

        <div style={styles.actions}>
          <button type="button" style={styles.backButton} onClick={() => navigate('/quote/start')}>
            Back
          </button>
          <button type="submit" style={buttonStyle}>
            Save and Continue
          </button>
        </div>
      </form>
    </AppLayout>
  );
};

const styles = {
  grid: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  },
  actions: {
    alignItems: 'flex-end',
    display: 'flex',
    gap: 10,
    gridColumn: '1 / -1',
    justifyContent: 'flex-end',
  },
  backButton: {
    background: '#eff7f2',
    border: '1px solid #bdd8c7',
    borderRadius: 8,
    color: '#236946',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 700,
    padding: '11px 18px',
  },
  errorBanner: {
    background: '#fff5f5',
    border: '1px solid #f3b8b8',
    borderRadius: 8,
    color: '#9e2e2e',
    marginBottom: 12,
    padding: '10px 12px',
  },
};

export default CustomerDetailsScreen;
